
import React, { useRef, useCallback, useEffect, useContext } from 'react';
import { useController } from 'react-hook-form';
import { v4 } from 'uuid';
import { OnboardingInteractionContext } from '../GeaoOnboarding/OnboardingInteractionTracking';
import { GtmEventType } from '../../util/gtmTracking';

/**
 * Layout types for CheckboxButtonGroup component
 * @readonly
 * @enum {string}
 */
export const LayoutTypes = Object.freeze({
    Grid: 'grid',
    Brick: 'brick',
    OneColumn: 'One Column',
    TwoColumn: 'Two Column',
    ThreeColumn: 'Three Column'
});

/**
 * CheckboxButtonGroup component that wraps the GDS checkbox-button-group web component
 * for use with react-hook-form. Provides form validation and error handling for
 * checkbox button groups with multiple layout options.
 * 
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.control - React Hook Form control object for form state management
 * @param {string} props.name - The name/key for this field in the form data
 * @param {Array<Object>} [props.options=[]] - Array of checkbox options
 * @param {string} props.options[].value - The value of the checkbox option
 * @param {string} [props.options[].label] - The label text for the option (fallback to text)
 * @param {string} [props.options[].text] - Alternative label text for the option
 * @param {string} [props.options[].description] - Optional description text for the option
 * @param {string} [props.options[].trackingId] - Static analytics name, never an entered value or display label
 * @param {string} [props.legend] - The legend text displayed for the checkbox group
 * @param {string} [props.descriptiveCopy] - Optional descriptive text displayed below the legend
 * @param {string} [props.layout=LayoutTypes.TwoColumn] - Layout style for the checkbox group
 * @param {React.ReactNode} [props.tooltip] - Optional tooltip content to display next to the legend
 * @param {string} [props.fieldsetAriaLabelledby] - ARIA labelledby attribute for the fieldset
 * @param {boolean} [props.fullWidth=false] - Whether to apply full-width styling to the button group
 * @returns {JSX.Element} The rendered CheckboxButtonGroup component
 * 
 * @example
 * // Basic usage with react-hook-form
 * <CheckboxButtonGroup 
 *   control={control} 
 *   name="preferences"
 *   options={[
 *     { value: 'email', label: 'Email notifications' },
 *     { value: 'sms', label: 'SMS notifications' }
 *   ]}
 * />
 * 
 * @example
 * // With custom layout and legend
 * <CheckboxButtonGroup 
 *   control={control} 
 *   name="features"
 *   legend="Select Features"
 *   layout={LayoutTypes.Grid}
 *   options={[
 *     { value: 'feature1', label: 'Feature 1', description: 'Description of feature 1' },
 *     { value: 'feature2', label: 'Feature 2', description: 'Description of feature 2' }
 *   ]}
 * />
 */
export default function CheckboxButtonGroup({
    control,
    name,
    options = [],
    legend,
    descriptiveCopy,
    layout = LayoutTypes.TwoColumn,
    tooltip,
    fieldsetAriaLabelledby,
    fullWidth = false,
    trackingId
}) {
    const trackInteraction = useContext(OnboardingInteractionContext);
    /** @type {React.MutableRefObject<HTMLElement>} Reference to the gds-checkbox-button-group web component */
    const gdsCheckboxButtonGroupRef = useRef();

    /**
     * React Hook Form controller for managing field state, validation, and error handling
     * @type {Object} field - Field methods and properties from react-hook-form
     * @type {Object} error - Field error information from fieldState
     */
    const { field, fieldState: { error } } = useController({
        control,
        name,
        defaultValue: []
    });

    /**
     * Handles value changes from the GDS checkbox-button-group component
     * Updates the form field value when checkboxes are selected/deselected
     * 
     * @param {CustomEvent} e - The value-change event from the GDS component
     * @param {Object} e.detail - Event detail object
     * @param {Array<string>} e.detail.value - Array of selected checkbox values
     */
    const handleValueChange = useCallback((e) => {
        const values = e.detail?.value || [];
        if (trackingId) {
            options.forEach(option => {
                const checked = values.includes(option.value);
                if (checked !== Boolean(field.value?.includes(option.value))) {
                    if (option.trackingId) {
                        trackInteraction?.(GtmEventType.CheckboxChange, `${trackingId}:${option.trackingId}`, { checked });
                    } else if (trackInteraction) {
                        console.warn('GTM checkbox option requires a static trackingId:', trackingId);
                    }
                }
            });
        }
        field.onChange(values);
    }, [field, options, trackingId, trackInteraction]);

    /**
     * Effect to set up and clean up event listener for value changes
     * Listens to the 'value-change' custom event from the GDS component
     */
    useEffect(() => {
        const element = gdsCheckboxButtonGroupRef.current;
        element?.addEventListener("value-change", handleValueChange);
        return () => {
            element?.removeEventListener("value-change", handleValueChange);
        };
    }, [handleValueChange]);

    return (
        <gds-checkbox-button-group
            data-onboarding-control-id={trackingId}
            ref={gdsCheckboxButtonGroupRef}
            group-id={`checkbox-button-group-${v4()}`}
            layout={layout}
            fieldset-aria-labelledby={fieldsetAriaLabelledby}
            full-width={fullWidth ? "true" : null}
        >
            {legend && (
                <span slot="legend">
                    {legend}
                    {tooltip && (
                        <gds-tooltip position="auto">
                            {tooltip}
                        </gds-tooltip>
                    )}
                </span>
            )}

            {descriptiveCopy && (
                <span slot="descriptive-copy">
                    {descriptiveCopy}
                </span>
            )}

            {options.map(option => (
                <gds-checkbox-button
                    key={option.value}
                    name={name}
                    value={option.value}
                    checked={field.value?.includes(option.value) ? "true" : null}
                >
                    <span slot="label">
                        {option.label || option.text}
                    </span>
                    {option.description && (
                        <p slot="description">{option.description}</p>
                    )}
                </gds-checkbox-button>
            ))}

            {error &&
                <span slot="error-message">{error.message}</span>
            }
        </gds-checkbox-button-group>
    );
}